import { Button, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useCallback } from 'react'

import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useVouchersBalance } from 'hooks/use_vouchers_balance'
import { PackageCredits } from './package_credits'
import { useBookingContext } from '@providers/booking'
import { useRoutes } from 'app/use_routes'
import { Hooks } from 'service-api'
import { useParams } from 'react-router-dom'
import { getServiceTypeByName } from '@shared/service_types'
import { PackageIcon } from 'components-ui/src/asset/package_icon'

export const ProfilePackages = () => {
  const bookingCtx = useBookingContext()
  const { navigateRoute } = useRoutes()
  const sessionCtx = useSessionContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user?.lupId || ''
  const { accountName: locationName = '' } = useParams()
  const { data: serviceTypes } = Hooks.useClientLocationServiceType(locationName)
  const packagesService = getServiceTypeByName('packages', serviceTypes?.results || [])

  const vouchers = useVouchersBalance(lupId, { cacheEnabled: true })

  const navigateToPackages = useCallback(() => {
    bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'searchResults' } })
    navigateRoute('searchOffersResults', { serviceTypeName: 'packages' })
  }, [bookingCtx, navigateRoute])

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" pl={isBelowMD ? 2 : 0}>
        {t('profile.packages.title')}
      </Typography>

      <Spacer height={2} />

      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h5" pl={isBelowMD ? 2 : 0}>
          {t('profile.packages.creditBalance')}
        </Typography>

        <Spacer height={2} />

        {vouchers.isFetching ? (
          <Skeleton variant="rectangular" width="100%" height={200} />
        ) : !vouchers.unusedVouchers.items.length ? (
          <Typography variant="body1" pl={isBelowMD ? 2 : 0}>
            {t('profile.packages.noPackages')}
          </Typography>
        ) : (
          <Fragment>
            <Stack direction="column" spacing={2}>
              {vouchers.unusedVouchers.items.map((item, idx) => (
                <Typography variant="body1" key={`${item.groupName}-${idx}`}>
                  {item.groupName}:
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{
                      backgroundColor: theme.palette.action.hover,
                      padding: '4px 8px',
                      ml: 1,
                      borderRadius: '4px',
                    }}
                  >
                    {t('profile.packages.remaining', { value: String(item.value) })}
                  </Typography>
                </Typography>
              ))}
            </Stack>
          </Fragment>
        )}

        {packagesService?.canBeStandalone && (
          <>
            <Spacer height={2} />
            <Button
              variant="text"
              color="primary"
              size="large"
              startIcon={<PackageIcon />}
              onClick={navigateToPackages}
            >
              {t('global.label.purchaseAPackage')}
            </Button>
          </>
        )}
      </Paper>

      <PackageCredits />
    </Box>
  )
}
